import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { ChatBubbleLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';

type FeedbackType = 'bug' | 'idea' | 'other';

const FeedbackButton: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<FeedbackType>('idea');
  const [includeUrl, setIncludeUrl] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setSubmitting(true);
    try {
      await apiClient.post('/feedback', {
        message: message.trim(),
        type,
        url: includeUrl ? window.location.href : null,
        user_agent: navigator.userAgent,
      });
      setSuccess(true);
      setOpen(false);
      setMessage('');
      setType('idea');
    } catch (err) {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating button - subtle and small */}
      <IconButton
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 44,
          height: 44,
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          color: '#888',
          zIndex: 1000,
          '&:hover': {
            backgroundColor: '#fafafa',
            color: '#667eea',
            borderColor: '#667eea',
          },
        }}
        aria-label="Send feedback"
      >
        <ChatBubbleLeftIcon style={{ width: 20, height: 20 }} />
      </IconButton>

      {/* Feedback dialog - compact and simple */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Send Feedback
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{ color: '#888' }}
          >
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, newType) => newType && setType(newType)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton
              value="bug"
              sx={{
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: '#fff0f0',
                  color: '#d32f2f',
                },
              }}
            >
              🐛 Bug
            </ToggleButton>
            <ToggleButton
              value="idea"
              sx={{
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: '#f0f7ff',
                  color: '#1976d2',
                },
              }}
            >
              💡 Idea
            </ToggleButton>
            <ToggleButton
              value="other"
              sx={{
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                },
              }}
            >
              💬 Other
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder={
              type === 'bug'
                ? "What happened? What did you expect?"
                : type === 'idea'
                ? "What would make this app better?"
                : "What's on your mind?"
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />

          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="includeUrl"
              checked={includeUrl}
              onChange={(e) => setIncludeUrl(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label
              htmlFor="includeUrl"
              style={{ fontSize: '0.85rem', color: '#666', cursor: 'pointer' }}
            >
              Include current page for context
            </label>
          </Box>

          {user && (
            <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
              Sending as {user.username}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!message.trim() || submitting}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
              },
            }}
          >
            {submitting ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success message */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ borderRadius: '10px' }}>
          Thanks for your feedback! 🙏
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar
        open={error}
        autoHideDuration={3000}
        onClose={() => setError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ borderRadius: '10px' }}>
          Failed to send. Try again?
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackButton;
